import { makeAutoObservable, runInAction } from 'mobx';
import { callApi, type Call, type CallParticipant } from '../api/call_api';

export class CallStore {
  // Calls indexed by key
  calls: Map<string, Call> = new Map();
  // Calls indexed by association (associated_type:associated_uuid -> call keys)
  callsByAssociation: Map<string, string[]> = new Map();
  // Participants indexed by call key
  participants: Map<string, CallParticipant[]> = new Map();

  // Loading states
  loading = false;
  participantsLoading = false;

  // Active call state
  activeCallKey: string | null = null;

  // Error state
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Computed getters
  get activeCall(): Call | undefined {
    return this.activeCallKey ? this.calls.get(this.activeCallKey) : undefined;
  }

  get activeCallParticipants(): CallParticipant[] {
    return this.activeCallKey
      ? this.participants.get(this.activeCallKey) || []
      : [];
  }

  getCallsByAssociation(
    associatedType: string,
    associatedUuid: string
  ): Call[] {
    const key = `${associatedType}:${associatedUuid}`;
    const callKeys = this.callsByAssociation.get(key) || [];
    return callKeys
      .map((k) => this.calls.get(k))
      .filter((c): c is Call => c !== undefined)
      .sort(
        (a, b) =>
          new Date(b.date_created).getTime() -
          new Date(a.date_created).getTime()
      );
  }

  getParticipants(callKey: string): CallParticipant[] {
    return this.participants.get(callKey) || [];
  }

  // Actions
  setActiveCall(callKey: string | null): void {
    this.activeCallKey = callKey;
  }

  clearError(): void {
    this.error = null;
  }

  /**
   * Fetch calls for an association (request or collab)
   */
  async fetchByAssociation(
    associatedType: string,
    associatedUuid: string,
    identityKey: string
  ): Promise<void> {
    const associationKey = `${associatedType}:${associatedUuid}`;

    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      let cursor: string | undefined;
      const allCallKeys: string[] = [];

      do {
        const response = await callApi.getByAssociation(
          associatedType,
          associatedUuid,
          identityKey,
          cursor
        );

        runInAction(() => {
          response.items.forEach((call) => {
            this.calls.set(call.key, call);
            allCallKeys.push(call.key);
          });
        });

        cursor = response.cursor || undefined;
      } while (cursor);

      runInAction(() => {
        this.callsByAssociation.set(associationKey, allCallKeys);
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch calls';
      });
    }
  }

  /**
   * Create a new call
   */
  async createCall(
    numberTo: string,
    associatedType: string,
    associatedUuid: string,
    identityKey: string
  ): Promise<Call | undefined> {
    runInAction(() => {
      this.error = null;
    });

    try {
      const call = await callApi.createCall(
        numberTo,
        associatedType,
        associatedUuid,
        identityKey
      );

      runInAction(() => {
        this.calls.set(call.key, call);
        this.activeCallKey = call.key;

        // Update association index
        const associationKey = `${associatedType}:${associatedUuid}`;
        const existing = this.callsByAssociation.get(associationKey) || [];
        this.callsByAssociation.set(associationKey, [call.key, ...existing]);
      });

      return call;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to create call';
      });
      return undefined;
    }
  }

  /**
   * Add a participant to a call
   */
  async addParticipant(
    numberTo: string,
    callKey: string,
    identityKey: string
  ): Promise<CallParticipant | undefined> {
    try {
      const participant = await callApi.addParticipant(
        numberTo,
        callKey,
        identityKey
      );

      runInAction(() => {
        const existing = this.participants.get(callKey) || [];
        this.participants.set(callKey, [...existing, participant]);
      });

      return participant;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to add participant';
      });
      return undefined;
    }
  }

  /**
   * Remove a participant from a call
   */
  async removeParticipant(
    callKey: string,
    callSid: string,
    identityKey: string
  ): Promise<boolean> {
    try {
      await callApi.removeParticipant(callKey, callSid, identityKey);

      runInAction(() => {
        const existing = this.participants.get(callKey) || [];
        this.participants.set(
          callKey,
          existing.filter((p) => p.twilio_call_sid !== callSid)
        );
      });

      return true;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to remove participant';
      });
      return false;
    }
  }

  /**
   * Fetch participants for a call
   */
  async fetchParticipants(
    callKey: string,
    identityKey: string
  ): Promise<void> {
    runInAction(() => {
      this.participantsLoading = true;
    });

    try {
      let cursor: string | undefined;
      const allParticipants: CallParticipant[] = [];

      do {
        const response = await callApi.getParticipants(
          callKey,
          identityKey,
          cursor
        );
        allParticipants.push(...response.items);
        cursor = response.cursor || undefined;
      } while (cursor);

      runInAction(() => {
        this.participants.set(callKey, allParticipants);
        this.participantsLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.participantsLoading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch participants';
      });
    }
  }

  /**
   * Receive realtime update for a call
   */
  receiveCallUpdate(call: Call): void {
    runInAction(() => {
      this.calls.set(call.key, call);
    });
  }

  /**
   * Receive realtime update for a participant
   */
  receiveParticipantUpdate(callKey: string, participant: CallParticipant): void {
    runInAction(() => {
      const existing = this.participants.get(callKey) || [];
      const index = existing.findIndex((p) => p.key === participant.key);
      if (index >= 0) {
        existing[index] = participant;
        this.participants.set(callKey, [...existing]);
      } else {
        this.participants.set(callKey, [...existing, participant]);
      }
    });
  }
}

export const callStore = new CallStore();
